import React, { useEffect, useState, useMemo } from 'react'
import { useOutletContext } from 'react-router-dom'
import * as walletService from '../../services/walletService'
import { getAffiliateMembers } from '../../services/affiliateService'
import type { AffiliateOutletContext } from './ModernAffiliateLayout'
import type { AffiliateMemberItem } from '../../services/affiliateService'
import './affiliateLayout.css'

function formatJoinDate(joinedAt: string): string {
  try {
    const d = new Date(joinedAt)
    return Number.isNaN(d.getTime()) ? joinedAt : d.toLocaleString('th-TH', { dateStyle: 'medium', timeStyle: 'short' })
  } catch {
    return joinedAt
  }
}

function maskMember(raw: string): string {
  const s = String(raw || '').trim()
  if (!s) return 'สมาชิก'
  if (s.length < 4) return s
  if (/^\d+$/.test(s)) {
    if (s.length >= 10) return s.slice(0, 3) + 'xxxxxx' + s.slice(-1)
    return s.slice(0, 2) + 'xxxxx' + s.slice(-1)
  }
  if (s.length <= 6) return s.slice(0, 1) + 'xxx' + s.slice(-1)
  return s.slice(0, 2) + '****' + s.slice(-3)
}

const AffiliateMemberPage: React.FC = () => {
  useOutletContext<AffiliateOutletContext>()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [members, setMembers] = useState<AffiliateMemberItem[]>([])
  const [searchKeyword, setSearchKeyword] = useState('')

  useEffect(() => {
    let cancelled = false
    async function load() {
      setLoading(true)
      setError(null)
      try {
        const list = await getAffiliateMembers()
        if (!cancelled) setMembers(list)
      } catch (e) {
        if (!cancelled) setError(e instanceof Error ? e.message : 'โหลดข้อมูลไม่สำเร็จ')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
  }, [])

  const filteredMembers = useMemo(() => {
    if (!searchKeyword.trim()) return members
    const q = searchKeyword.trim().toLowerCase()
    return members.filter(
      (m) =>
        (m.username || '').toLowerCase().includes(q) ||
        (m.displayName || '').toLowerCase().includes(q) ||
        maskMember(m.username || '').toLowerCase().includes(q)
    )
  }, [members, searchKeyword])

  const totalBetAmount = 0

  if (loading) {
    return (
      <div className="htd-aff__loading">
        <div className="spinner-border" role="status" />
        <p>กำลังโหลดข้อมูล...</p>
      </div>
    )
  }
  if (error) {
    return (
      <div className="htd-aff__shell">
        <h3>โหลดข้อมูลไม่สำเร็จ</h3>
        <p>{error}</p>
      </div>
    )
  }

  return (
    <div className="htd-aff__stack">
      <section className="htd-aff__stats" aria-label="สรุปสมาชิก">
        <div className="htd-aff__stat">
          <span>จำนวนสมาชิก (คน)</span>
          <strong>{members.length}</strong>
        </div>
        <div className="htd-aff__stat">
          <span>ยอดแทงรวม (บาท)</span>
          <strong className="is-accent">{walletService.formatCurrency(totalBetAmount)}</strong>
        </div>
      </section>

      <section className="htd-aff__panel">
        <div className="htd-aff__panel-head">
          <div className="d-flex align-items-center gap-2">
            <span className="htd-aff__head-icon" aria-hidden>
              <i className="fas fa-users" />
            </span>
            <div>
              <h3>สมาชิกในเครือข่าย</h3>
              <p>ค้นหาและดูรายละเอียดสมาชิกที่สมัครผ่านลิงก์ของคุณ</p>
            </div>
          </div>
        </div>
        <div className="htd-aff__link-row">
          <input
            type="text"
            className="htd-aff__input"
            placeholder="ค้นหา username ที่ถูกปิดบัง..."
            value={searchKeyword}
            onChange={(e) => setSearchKeyword(e.target.value)}
          />
          <button
            type="button"
            className="htd-aff__copy-btn"
            onClick={() => setSearchKeyword(searchKeyword.trim())}
          >
            <i className="fas fa-search me-1" />
            ค้นหา
          </button>
        </div>
      </section>

      {filteredMembers.length === 0 ? (
        <div className="htd-aff__empty">
          <i className="fas fa-user-friends" />
          <h4>ไม่มีรายการสมาชิก</h4>
        </div>
      ) : (
        <div className="htd-aff__grid-2">
          {filteredMembers.map((member, index) => (
            <article key={`${member.username}-${index}`} className="htd-aff__member-card">
              <div className="htd-aff__member-top">
                <div>
                  <h3>{maskMember(member.username || member.displayName || 'สมาชิก')}</h3>
                  <small>แสดง username แบบปิดบังบางส่วน</small>
                </div>
                <span className="htd-aff__rank">#{index + 1}</span>
              </div>
              <div className="htd-aff__meta">
                สมัครเมื่อวันที่ {formatJoinDate(member.joinedAt)}{' '}
                <span className="htd-aff__status">{member.status || 'สมาชิกปกติ'}</span>
              </div>
              <div className="htd-aff__metrics">
                <div className="htd-aff__metric">
                  <span>เดิมพันทั้งหมด</span>
                  <strong>{walletService.formatCurrency(0)}</strong>
                </div>
                <div className="htd-aff__metric htd-aff__metric--muted">
                  <span>คอมมิชชั่น</span>
                  <strong>{walletService.formatCurrency(0)}</strong>
                </div>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  )
}

export default AffiliateMemberPage
